import moment from "moment";
import React, { useState } from "react";
import { Waypoint } from "react-waypoint";

import { NetworkStatus, useReactiveVar } from "@apollo/client";
import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import uniqBy from "lodash/uniqBy";

import {
  GetEventsPendingHumanValidationByTenantQuery,
  ModelType,
  ValidationState,
} from "../../../API";
import StatusToggle from "../../../common/components/item/StatusToggle";
import CustomerSelect from "../../../common/components/layout/components/CustomerSelect";
import {
  useGetHumanValidatorByTenantTableData,
  type PendingEventRow,
} from "../hooks/useHumanValidatorByTenantTableData";
import { useTakeToValidate } from "../hooks/useTakeToValidate";
import { humanValidatorTableVariable } from "../variables/humanValidatorTable";

const PRELOAD_ITEMS = 25;

const CCAdminHumanValidatorTable = (): JSX.Element => {
  const { tableData, showDetails } = useReactiveVar(
    humanValidatorTableVariable
  );
  const [useCustomerSelectValue, setUseCustomerSelectValue] = useState(false);

  const { rows, nextToken, loading, networkStatus, fetchMore } =
    useGetHumanValidatorByTenantTableData(useCustomerSelectValue);

  const { takeToValidate } = useTakeToValidate();

  const apiMutationTakeEventToValidate = (
    row: PendingEventRow,
    customerId: string,
    eventId: string
  ): void => {
    takeToValidate({
      variables: {
        customerId,
        eventId,
      },
    })
      .then((response): void => {
        humanValidatorTableVariable({
          tableData: {
            ...row,
            ...response.data?.takeEventToValidate,
          },
          showDetails: true,
        });
      })
      .catch((error): void => {
        console.error(error);
      });
  };

  const handleRowClick = (
    _event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: PendingEventRow
  ): void => {
    if (showDetails) {
      return;
    }

    apiMutationTakeEventToValidate(row, row.customerId, row.eventId);
  };

  const networkStatusChanged = [NetworkStatus.fetchMore].includes(
    networkStatus
  );

  return (
    <>
      <Box sx={{ marginBottom: "1.5em" }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            padding: "1em",
            backgroundColor: theme => theme.palette.otherBackgroundLight.main,
            border: theme =>
              `1px solid ${theme.palette.otherOutlineBorder.main}`,
            borderRadius: "12px",
          }}
        >
          <Box sx={{ minWidth: "240px" }}>
            <CustomerSelect />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2Regular"
              sx={{ color: theme => theme.palette.text.secondary }}
            >
              Use Customer Select
            </Typography>
            <StatusToggle
              checked={useCustomerSelectValue}
              handleChange={(event): void => {
                setUseCustomerSelectValue(event.target.checked);
              }}
            />
          </Stack>
        </Stack>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Event ID</TableCell>
            <TableCell align="right">Date</TableCell>
            <TableCell align="right">Model</TableCell>
            <TableCell align="right">Customer</TableCell>
            <TableCell align="right">Location</TableCell>
            <TableCell align="right">Camera</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {React.Children.toArray(
            rows.map((row, index): JSX.Element => {
              const fromUTCToLocalDate = moment
                .utc(row.date)
                .local()
                .format("YYYY-MM-DD HH:mm");

              const date = row.date ? fromUTCToLocalDate : "n/a";

              const renameGL =
                row.model === ModelType.gasleak ? "leak" : row.model;

              return (
                <React.Fragment key={row.rowId}>
                  <TableRow
                    key={row.rowId}
                    hover
                    selected={row.rowId === tableData?.rowId}
                    onClick={(event): void => handleRowClick(event, row)}
                    sx={{
                      cursor:
                        showDetails ||
                        row.validationState === ValidationState.IN_VALIDATION
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    <TableCell>{row.rowId}</TableCell>
                    <TableCell align="right">{date}</TableCell>
                    <TableCell align="right">{renameGL}</TableCell>
                    <TableCell align="right" sx={{ wordBreak: "break-all" }}>
                      {row.customerId.split("#").pop()}
                    </TableCell>
                    <TableCell align="right" sx={{ wordBreak: "break-all" }}>
                      {row.locationId.split("#").pop()}
                    </TableCell>
                    <TableCell align="right">{row.cameraName}</TableCell>
                  </TableRow>
                  <Waypoint
                    onEnter={(): void => {
                      if (
                        nextToken &&
                        (index === rows.length - PRELOAD_ITEMS ||
                          rows.length < PRELOAD_ITEMS)
                      ) {
                        void fetchMore({
                          variables: {
                            nextToken,
                          },
                          updateQuery: (
                            pv: GetEventsPendingHumanValidationByTenantQuery,
                            {
                              fetchMoreResult,
                            }: {
                              fetchMoreResult: GetEventsPendingHumanValidationByTenantQuery;
                            }
                          ): GetEventsPendingHumanValidationByTenantQuery => {
                            const items = uniqBy(
                              [
                                ...(pv?.getEventsPendingHumanValidationByTenant
                                  .items || []),
                                ...fetchMoreResult
                                  .getEventsPendingHumanValidationByTenant
                                  .items,
                              ],
                              (item): string | undefined => item?.id
                            );

                            return {
                              getEventsPendingHumanValidationByTenant: {
                                ...fetchMoreResult.getEventsPendingHumanValidationByTenant,
                                items,
                                nextToken:
                                  fetchMoreResult
                                    .getEventsPendingHumanValidationByTenant
                                    .nextToken,
                              },
                            };
                          },
                        });
                      }
                    }}
                  />
                </React.Fragment>
              );
            })
          )}

          {(loading || networkStatusChanged) && (
            <TableRow>
              <TableCell colSpan={6}>
                {[...Array(6)].map(
                  (_, index): JSX.Element => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      sx={{ my: 3 }}
                    />
                  )
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

export default CCAdminHumanValidatorTable;
